import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Subject, Subscription } from 'rxjs';

import { CompanySettings } from '../../settings/model/company-settings';
import { SessionService } from '../../shared/session/session.service';
import { Version } from '../../shared/version/version';
import { User } from '../../user-management/users/user';
import { UsersService } from '../../user-management/users/users.service';
import * as fromCompanySettingsStore from '../../settings/store';
import { Store } from '@ngrx/store';
import { takeUntil, map } from 'rxjs/operators';
import { TokenService } from '../../authentication/token.service';
import { environment } from 'environments/environment';
import { datadogLog } from '../../shared/utils/datadog';

@Component({
  selector: 'ls-external-login',
  templateUrl: './external-login.component.html',
  styleUrls: ['./external-login.component.scss']
})
export class ExternalLoginComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<Subscription> = new Subject();
  public ulp: string;
  public url: string;
  public loginFrom: string;
  public username: string;
  constructor(
    private route: ActivatedRoute,
    private user: UsersService,
    private tokenService: TokenService,
    private router: Router,
    private session: SessionService,
    private store: Store<fromCompanySettingsStore.CompanyState>
  ) {}

  public ngOnInit(): void {
    this.url = location.href;
    this.username = this.route.snapshot.queryParamMap.get('username');
    const token: string = this.route.snapshot.queryParamMap.get('token');
    const companyId: string = this.route.snapshot.queryParamMap.get(
      'companyId'
    );
    this.ulp = this.route.snapshot.queryParamMap.get('ulp');
    this.loginFrom = this.route.snapshot.queryParamMap.get('loginFrom');
    const version: string = this.route.snapshot.queryParamMap.get('version');
    const redirect: string = this.route.snapshot.queryParamMap.get('redirect');
    const deviceId: string = this.route.snapshot.queryParamMap.get('deviceId');

    if (deviceId) {
      datadogLog('warn', 'deviceId:' + deviceId, {
        data: {
          deviceId,
          url: this.url.replace(/token=[^&]*/, 'token=***')
        }
      });
    }
    datadogLog('warn', 'login', {
      data: {
        version: !!version ? version : 'N/A',
        redirect: !!redirect ? redirect : 'N/A',
        loginFrom: !!this.loginFrom ? this.loginFrom : 'N/A',
        token: !!token
      }
    });
    if ((!!token || this.loginFrom === 'pos') && !!version && !redirect) {
      datadogLog('info', 'login-from-app', {});
      this.loginFromApp(token, Version.fromString(version));
    } else if (!!this.username) {
      this.loginFromLiteServer(
        this.username,
        Number(companyId),
        Version.fromString(version)
      );
    } else if (!!token) {
      this.loginFromPartnerOrWebsite(token);
    } else {
      // Regular login from web browser
      const baseUrl = new URL(
        `${location.origin.replace('-web', '')}${environment.authService}`
      );
      if (
        window.location.hostname &&
        window.location.hostname === 'localhost'
      ) {
        baseUrl.searchParams.append('localhost', 'true');
      }

      location.href = baseUrl.href;
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private loginFromApp(token: string, appVersion: Version): void {
    this.session.currentAppVersion = appVersion;

    this.session.setLoginFromAppLegacyCookies(appVersion.toString());

    if (this.session.minimumAppVersion.isGreaterThen(appVersion)) {
      this.router.navigate(['/update-warning']);
      return;
    }

    // Token is passed as query param when app opens manager first time
    // When unified login page is disabled, this code is executed again but without a token
    if (token) {
      this.tokenService.token = token;

      combineLatest(
        fromCompanySettingsStore.getSettingsFromStore(this.store),
        this.user.getUser()
      )
        .pipe(
          map(([company, user]: [CompanySettings, User]) => {
            this.tokenService.token = null;
            if (company.autoLogin) {
              this.session.createSession(token);
            } else if (user.type !== 'admin') {
              this.router.navigate([
                '/login',
                {
                  companyId: company.id,
                  username: user.username,
                  loginFrom: 'pos',
                  ulp: this.ulp ? this.ulp : '',
                  version: appVersion.toString()
                }
              ]);
            } else {
              this.router.navigate([
                '/login',
                {
                  username: user.username,
                  loginFrom: 'pos',
                  ulp: this.ulp ? this.ulp : '',
                  version: appVersion.toString()
                }
              ]);
            }
          }),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe();
    } else if (this.loginFrom === 'pos') {
      this.router.navigate([
        '/login',
        {
          username: this.username,
          loginFrom: 'pos',
          ulp: this.ulp ? this.ulp : '',
          version: appVersion.toString()
        }
      ]);
    }
  }

  private loginFromLiteServer(
    username: string,
    companyId: number,
    appVersion: Version
  ): void {
    this.session.currentAppVersion = appVersion;
    if (this.session.minimumAppVersion.isGreaterThen(appVersion)) {
      this.router.navigate(['/update-warning']);
      return;
    } else {
      if (!!companyId) {
        this.router.navigate([
          '/login',
          {
            companyId: companyId,
            username: username,
            loginFrom: 'pos',
            ulp: this.ulp ? this.ulp : '',
            version: appVersion.toString()
          }
        ]);
      } else {
        this.router.navigate([
          '/login',
          {
            username: username,
            loginFrom: 'pos',
            ulp: this.ulp ? this.ulp : '',
            version: appVersion.toString()
          }
        ]);
      }
    }
  }

  private loginFromPartnerOrWebsite(token: string): void {
    this.session.createSession(token);
  }
}
